import styles from './index.module.css'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { getTextLinesCount } from '@disco/utils/dom'

/**
 * A component that displays text with a configurable number of visible lines and a toggle to expand/collapse
 * @param text - The text content to display
 * @param visibleLines - The number of lines to show initially (defaults to 5)
 * @param expandLabel - Custom label for the expand button (defaults to 'Read More')
 * @param collapseLabel - Custom label for the collapse button (defaults to 'Read Less')
 * @param inline - If true, positions the button inline at the end of the last visible line (defaults to false)
 * @returns A text display with expand/collapse functionality based on line count
 */
const ExpandableText = ({
  text,
  visibleLines = 5,
  expandLabel = 'Read More',
  collapseLabel = 'Read Less',
  inline = false,
}: {
  text: string
  visibleLines: number
  expandLabel?: string
  collapseLabel?: string
  inline?: boolean
}): React.ReactNode => {
  const [expanded, setExpanded] = useState(false)
  const invisibleContainer = useRef(null)
  const [linesCount, setLinesCount] = useState(0)
  const textParagraphs = text?.trim()?.split('\n')

  const showButton = linesCount > visibleLines
  const showInlineButton = inline && expanded && showButton

  const messageText = textParagraphs?.map((text, index, arr) => {
    const isLast = index === arr.length - 1
    return (
      <p className={styles.textParagraphs} key={index}>
        {text}
        {isLast && showInlineButton ? (
          <>
            {' '}
            <button
              className={styles.buttonInlineFlow}
              onClick={() => setExpanded(!expanded)}
            >
              {collapseLabel}
            </button>
          </>
        ) : (
          <>&nbsp;</>
        )}
      </p>
    )
  })

  const messageTextForMeasure = useMemo(
    () =>
      textParagraphs?.map((paragraph, index) => {
        return (
          <p className={styles.textParagraphs} key={index}>
            {paragraph}&nbsp;
          </p>
        )
      }),
    [text],
  )

  useEffect(() => {
    if (!messageTextForMeasure || !invisibleContainer.current) return
    setLinesCount(getTextLinesCount(invisibleContainer.current))
  }, [messageTextForMeasure])

  if (!messageText) return null

  return (
    <>
      {/* invisible text block to calc text height */}
      <div
        className={`${styles.text} ${styles.invisibleText}`}
        style={{
          WebkitLineClamp: 'unset',
        }}
        ref={invisibleContainer}
      >
        {messageTextForMeasure}
      </div>

      <div
        className={styles.text}
        style={{
          WebkitLineClamp:
            expanded || linesCount === 0 ? 'unset' : visibleLines,
        }}
      >
        {messageText}
      </div>
      {showButton && !showInlineButton && (
        <button
          className={inline && !expanded ? styles.buttonInline : styles.button}
          onClick={() => setExpanded(!expanded)}
        >
          <span>
            {expanded
              ? collapseLabel
              : inline
              ? `... ${expandLabel}`
              : expandLabel}
          </span>
        </button>
      )}
    </>
  )
}

export default ExpandableText
